import { get, orderBy } from 'lodash-es'
import type { CustomDataTableHierarchyLevel, DynamicDatatableProperty } from '@canvas-builder/types/canvas'

export interface ProcessedHierarchicalRow {
  id: string
  level: number
  type: 'group' | 'data'
  groupValue?: string
  data?: Record<string, any>
  children?: ProcessedHierarchicalRow[]
  isExpanded?: boolean
}

export interface RowWithAncestors extends ProcessedHierarchicalRow {
  ancestorGroups?: ProcessedHierarchicalRow[]
  isInnermostGroup?: boolean
}

/**
 * Process hierarchical data based on configured levels
 */
export function processHierarchicalData(
  sourceData: any,
  hierarchy: CustomDataTableHierarchyLevel[],
  columns?: DynamicDatatableProperty[],
  userSortConfig?: { key: string; direction: 'asc' | 'desc' },
): ProcessedHierarchicalRow[] {
  if (!hierarchy.length || !sourceData) return []

  // Use user sort config if provided, otherwise fall back to default from columns
  let sortConfig = userSortConfig
  if (!sortConfig) {
    const sortColumn = columns?.find((col) => col.sort?.default && col.sort?.enabled)
    sortConfig = sortColumn
      ? { key: sortColumn.key, direction: sortColumn.sort?.direction || ('asc' as 'asc' | 'desc') }
      : undefined
  }

  const dataLevel = hierarchy[hierarchy.length - 1]

  // If only one level (data), return flat structure
  if (hierarchy.length === 1) {
    const rawData = get(sourceData, dataLevel.path, [])
    if (!Array.isArray(rawData)) return []

    let data = rawData.map((item, idx) => ({
      id: `data-${idx}`,
      level: 0,
      type: 'data' as const,
      data: item,
    }))

    // Apply sorting if configured
    if (sortConfig) {
      data = orderBy(data, [(row) => get(row.data, sortConfig.key)], [sortConfig.direction])
    }

    return data
  }

  // Process hierarchical structure
  return buildGroupedStructure(sourceData, hierarchy, 0, '', sortConfig)
}

/**
 * Build nested group structure recursively
 */
function buildGroupedStructure(
  sourceData: any,
  hierarchy: CustomDataTableHierarchyLevel[],
  currentLevel: number,
  parentId: string,
  sortConfig?: { key: string; direction: 'asc' | 'desc' },
): ProcessedHierarchicalRow[] {
  const currentHierarchy = hierarchy[currentLevel]
  if (!currentHierarchy) return []

  const currentData = get(sourceData, currentHierarchy.path, [])
  if (!Array.isArray(currentData)) return []

  const isLastLevel = currentLevel === hierarchy.length - 1

  return currentData.map((item, idx) => {
    const itemId = `${parentId}${currentLevel}-${idx}`

    if (isLastLevel) {
      // This is the data level
      return {
        id: itemId,
        level: currentLevel,
        type: 'data' as const,
        data: item,
      }
    }

    // This is a group level
    const groupValue = currentHierarchy.group_by_key ? get(item, currentHierarchy.group_by_key, '') : ''
    const children = buildChildrenForGroup(item, hierarchy, currentLevel + 1, itemId, sortConfig)

    return {
      id: itemId,
      level: currentLevel,
      type: 'group' as const,
      groupValue: String(groupValue),
      children,
      isExpanded: true, // Default to expanded
    }
  })
}

/**
 * Build children for a group item, with support for skipping missing intermediate levels
 */
function buildChildrenForGroup(
  parentItem: any,
  hierarchy: CustomDataTableHierarchyLevel[],
  nextLevel: number,
  parentId: string,
  sortConfig?: { key: string; direction: 'asc' | 'desc' },
): ProcessedHierarchicalRow[] {
  const nextHierarchy = hierarchy[nextLevel]
  if (!nextHierarchy) return []

  // Extract the relative path for children
  const parentPath = hierarchy[nextLevel - 1].path
  const childPath = nextHierarchy.path

  // Get the relative path (e.g., 'groups.subgroups' -> 'subgroups')
  const relativePath = childPath.replace(parentPath + '.', '')

  const childrenData = get(parentItem, relativePath, [])

  // If children don't exist at this level and we're not at the last level,
  // try to skip to the next level (handles optional intermediate groups)
  const isLastLevel = nextLevel === hierarchy.length - 1
  if ((!Array.isArray(childrenData) || childrenData.length === 0) && !isLastLevel) {
    const skippedResult = trySkipToNextLevel(parentItem, hierarchy, nextLevel, parentId, sortConfig)
    if (skippedResult.length > 0) return skippedResult
  }

  if (!Array.isArray(childrenData)) return []

  let rows = childrenData.map((item, idx) => {
    const itemId = `${parentId}-${nextLevel}-${idx}`

    if (isLastLevel) {
      return {
        id: itemId,
        level: nextLevel,
        type: 'data' as const,
        data: item,
      }
    }

    const groupValue = nextHierarchy.group_by_key ? get(item, nextHierarchy.group_by_key, '') : ''
    const children = buildChildrenForGroup(item, hierarchy, nextLevel + 1, itemId, sortConfig)

    return {
      id: itemId,
      level: nextLevel,
      type: 'group' as const,
      groupValue: String(groupValue),
      children,
      isExpanded: true,
    }
  })

  // Apply sorting only to data rows (last level)
  if (isLastLevel && sortConfig) {
    rows = orderBy(rows, [(row) => get(row.data, sortConfig.key)], [sortConfig.direction])
  }

  return rows
}

/**
 * Attempt to skip an intermediate level and access the next level directly
 * This handles cases where some branches have different depths
 * Example: Some groups have subgroups.funds, others have funds directly
 */
function trySkipToNextLevel(
  parentItem: any,
  hierarchy: CustomDataTableHierarchyLevel[],
  currentLevel: number,
  parentId: string,
  sortConfig?: { key: string; direction: 'asc' | 'desc' },
): ProcessedHierarchicalRow[] {
  // Try to find the next available level with data
  for (let skipLevel = currentLevel + 1; skipLevel < hierarchy.length; skipLevel++) {
    const skipHierarchy = hierarchy[skipLevel]

    // Extract just the property name from the path (last segment)
    // e.g., 'fund_data.groups.subgroups.funds' -> 'funds'
    const pathSegments = skipHierarchy.path.split('.')
    const propertyName = pathSegments[pathSegments.length - 1]

    // Check if this property exists directly on the parent item
    const skipData = get(parentItem, propertyName, [])

    if (Array.isArray(skipData) && skipData.length > 0) {
      // Found data at this level - render it directly
      // If this is the data level, create data rows
      // If this is a group level, create group rows but continue from this point
      const isDataLevel = skipHierarchy.type === 'data'

      let rows = skipData.map((item, idx) => {
        const itemId = `${parentId}-skip-${skipLevel}-${idx}`

        if (isDataLevel) {
          return {
            id: itemId,
            level: currentLevel, // Keep at the current level to maintain proper indentation
            type: 'data' as const,
            data: item,
          }
        }

        // It's a group level - continue building from here
        const groupValue = skipHierarchy.group_by_key ? get(item, skipHierarchy.group_by_key, '') : ''

        return {
          id: itemId,
          level: currentLevel, // Keep at the current level
          type: 'group' as const,
          groupValue: String(groupValue),
          isExpanded: true,
          children: buildChildrenForGroup(item, hierarchy, skipLevel + 1, itemId, sortConfig),
        }
      })

      // Apply sorting only to data rows
      if (isDataLevel && sortConfig) {
        rows = orderBy(rows, [(row) => get(row.data, sortConfig.key)], [sortConfig.direction])
      }

      return rows
    }
  }

  return []
}

/**
 * Flatten hierarchical structure for rendering
 * @param rows - The hierarchical rows to flatten
 * @param includeCollapsed - Whether to include collapsed rows
 * @param forceExpandAll - Force all groups to be expanded (useful for print mode)
 */
export function flattenForRendering(
  rows: ProcessedHierarchicalRow[],
  includeCollapsed = false,
  forceExpandAll = false,
): RowWithAncestors[] {
  const result: RowWithAncestors[] = []

  function traverse(row: ProcessedHierarchicalRow, ancestors: ProcessedHierarchicalRow[] = []) {
    // Check if this is an innermost group (group whose children are all data rows)
    const isInnermostGroup =
      row.type === 'group' &&
      row.children &&
      row.children.length > 0 &&
      row.children.every((child) => child.type === 'data')

    // Add row with ancestor tracking
    result.push({
      ...row,
      ancestorGroups: ancestors.length > 0 ? [...ancestors] : undefined,
      isInnermostGroup,
    })

    if (row.type === 'group' && row.children) {
      // Force expand all in print mode, or respect expand state
      if (forceExpandAll || includeCollapsed || row.isExpanded) {
        // Add current group to ancestors for children
        const newAncestors = [...ancestors, row]
        row.children.forEach((child) => traverse(child, newAncestors))
      }
    }
  }

  rows.forEach((row) => traverse(row))
  return result
}

/**
 * Toggle expand/collapse state of a group row
 */
export function toggleRowExpansion(rows: ProcessedHierarchicalRow[], rowId: string): ProcessedHierarchicalRow[] {
  return rows.map((row) => {
    if (row.id === rowId) {
      return {
        ...row,
        isExpanded: !row.isExpanded,
      }
    }

    if (row.children) {
      return {
        ...row,
        children: toggleRowExpansion(row.children, rowId),
      }
    }

    return row
  })
}
